import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import { Toast } from '@/components/common';
import {
  fetchGetMyApplications,
  fetchSubmitApplication,
  fetchCopyApplication,
  fetchDeleteApplication,
} from '@/service/api';
import { useChannelStore } from '@/stores';
import { useDict } from '@/hooks/useDict';

interface StatusConfigItem {
  label: string;
  color: string;
  focus: string;
}

const statusConfig: Record<string, StatusConfigItem> = {
  DRAFT: {
    label: '草稿',
    color: 'bg-gray-100 text-gray-700 border border-gray-200/60',
    focus: '六步申请未全完成，可检查缺失项并继续编辑或直接提交',
  },
  PENDING_REVIEW: {
    label: '待审核',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    focus: '提交摘要与材料复核中，请关注审核时间线',
  },
  PENDING: {
    label: '待审核',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    focus: '提交摘要与材料复核中，请关注审核时间线',
  },
  UNDER_REVIEW: {
    label: '待审核',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    focus: '提交摘要与材料复核中，请关注审核时间线',
  },
  IN_REVIEW: {
    label: '待审核',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    focus: '提交摘要与材料复核中，请关注审核时间线',
  },
  SUBMITTED: {
    label: '待审核',
    color: 'bg-amber-100 text-amber-800 border border-amber-200/60',
    focus: '已提交平台审核，请等待工作人员查阅复核',
  },
  SUPPLEMENT_REQUIRED: {
    label: '需补充',
    color: 'bg-orange-100 text-orange-800 border border-orange-200/60',
    focus: '根据审核意见，仅需补充指定范围的材料或剧集',
  },
  NEED_SUPPLEMENT: {
    label: '需补充',
    color: 'bg-orange-100 text-orange-800 border border-orange-200/60',
    focus: '根据审核意见，仅需补充指定范围的材料或剧集',
  },
  REJECTED: {
    label: '已驳回',
    color: 'bg-red-50 text-red-600 border border-red-200/60',
    focus: '审核未通过，可查看详细驳回原因或复制为新申请',
  },
  REFUSED: {
    label: '已驳回',
    color: 'bg-red-50 text-red-600 border border-red-200/60',
    focus: '审核未通过，可查看详细驳回原因或复制为新申请',
  },
  APPROVED: {
    label: '已通过',
    color: 'bg-emerald-100 text-emerald-800 border border-emerald-200/60',
    focus: '发行申请审核通过，待转换并生成发行项目',
  },
  PASSED: {
    label: '已通过',
    color: 'bg-emerald-100 text-emerald-800 border border-emerald-200/60',
    focus: '发行申请审核通过，待转换并生成发行项目',
  },
  CONVERTED_TO_PROJECT: {
    label: '已转项目',
    color: 'bg-blue-100 text-blue-700 border border-blue-200/60',
    focus: '已成功转换为发行项目，可直接跳转查看关联项目',
  },
  SIGNED: {
    label: '已转项目',
    color: 'bg-blue-100 text-blue-700 border border-blue-200/60',
    focus: '已成功转换为发行项目，可直接跳转查看关联项目',
  },
  COMPLETED: {
    label: '已转项目',
    color: 'bg-blue-100 text-blue-700 border border-blue-200/60',
    focus: '已成功转换为发行项目，可直接跳转查看关联项目',
  },
};

function formatDate(dateStr?: string) {
  if (!dateStr || dateStr === '-') return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return dateStr;
  }
}

function matchStatus(status: string | undefined, filter: string) {
  if (filter === 'all') return true;
  const s = (status || '').toUpperCase();
  if (filter === 'draft') return s === 'DRAFT';
  if (filter === 'pending_review' || filter === 'pending') {
    return ['PENDING', 'PENDING_REVIEW', 'UNDER_REVIEW', 'IN_REVIEW', 'SUBMITTED', 'REVIEWING'].includes(s);
  }
  if (filter === 'supplement_required' || filter === 'supplement') {
    return ['SUPPLEMENT_REQUIRED', 'NEED_SUPPLEMENT'].includes(s);
  }
  if (filter === 'rejected') return ['REJECTED', 'REFUSED'].includes(s);
  if (filter === 'approved') return ['APPROVED', 'PASSED'].includes(s);
  if (filter === 'converted_to_project' || filter === 'converted' || filter === 'signed') {
    return ['CONVERTED_TO_PROJECT', 'SIGNED', 'COMPLETED'].includes(s);
  }
  return s === filter.toUpperCase();
}

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const { getLabel: getContentTypeLabel } = useDict('CONTENT_TYPE');
  const [activeStatus, setActiveStatus] = useState<string>('all');

  const formatContentType = (code?: string) => {
    if (!code) return '';
    const presetMap: Record<string, string> = {
      SHORT_DRAMA: '短剧',
      MICRO_MOVIE: '微电影',
      ANIMATION: '动漫',
      OTHER: '其他',
    };
    return getContentTypeLabel(code, presetMap[code] || code);
  };
  const [applications, setApplications] = useState<Api.Application.ApplicationListItemResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [deleteConfirmApp, setDeleteConfirmApp] = useState<Api.Application.ApplicationListItemResponse | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const fetchedRef = useRef(false);

  const showToast = (text: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ text, type });
  };

  const loadApplications = (force = false) => {
    if (!force && fetchedRef.current) return;
    fetchedRef.current = true;

    setLoading(true);
    setError(null);

    fetchGetMyApplications()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.items || [];
        setApplications(list);
      })
      .catch((err) => {
        setError(err?.message || '获取发行申请列表失败');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const { fetchChannels, getChannelNameById } = useChannelStore();

  useEffect(() => {
    loadApplications();
    fetchChannels();
  }, [fetchChannels]);

  const handleSubmit = async (appId: string) => {
    setActionLoadingId(appId);
    try {
      await fetchSubmitApplication(appId);
      showToast('发行申请提交成功，请等待平台审核', 'success');
      loadApplications(true);
    } catch (err: any) {
      showToast(err?.message || '提交发行申请失败', 'error');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCopy = async (appId: string) => {
    setActionLoadingId(appId);
    try {
      const res = await fetchCopyApplication(appId);
      showToast('已成功复制为新申请草稿', 'success');
      if (res?.id) {
        navigate(`/apply?id=${res.id}`);
      } else {
        loadApplications(true);
      }
    } catch (err: any) {
      showToast(err?.message || '复制发行申请失败', 'error');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmApp?.id) return;
    const appId = deleteConfirmApp.id;
    setActionLoadingId(appId);
    try {
      await fetchDeleteApplication(appId);
      showToast('申请草稿删除成功', 'success');
      setDeleteConfirmApp(null);
      loadApplications(true);
    } catch (err: any) {
      showToast(err?.message || '删除申请草稿失败', 'error');
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredApplications = applications.filter((app) => matchStatus(app.status, activeStatus));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* 弹出消息提示 Toast Component */}
      {toast && (
        <Toast
          message={toast.text}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* 二次确认删除 Modal */}
      {deleteConfirmApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-100">
            <div className="flex items-center gap-3 text-red-600 mb-3">
              <i className="ri-delete-bin-line text-2xl"></i>
              <h3 className="font-bold text-base text-foreground-950">删除草稿确认</h3>
            </div>
            <p className="text-xs text-foreground-600 mb-6 leading-relaxed">
              确定要删除发行申请草稿「<span className="font-semibold text-foreground-900">{deleteConfirmApp.title || '未命名短剧'}</span>」吗？删除后数据不可恢复。
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmApp(null)}
                className="px-4 py-2 rounded-lg text-xs font-medium border border-gray-200 text-foreground-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={actionLoadingId === deleteConfirmApp.id}
                className="px-4 py-2 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {actionLoadingId === deleteConfirmApp.id && <i className="ri-loader-4-line animate-spin"></i>}
                <span>确认删除</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pt-28 sm:pt-32">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <Link
                to="/projects"
                className="inline-flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-600 mb-2 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                <span>返回发行项目</span>
              </Link>
              <h1 className="font-heading font-bold text-xl text-foreground-950">发行申请</h1>
              <p className="text-xs text-foreground-500 mt-1">管理您的短剧发行申请与审核进度</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/projects"
                className="px-4 py-2.5 rounded-lg border border-background-300/60 text-foreground-700 text-sm font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
              >
                <i className="ri-arrow-left-line"></i>
                <span>返回发行项目</span>
              </Link>
              <Link
                to="/apply"
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
              >
                新建发行申请
              </Link>
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {[
              { key: 'all', label: '全部' },
              { key: 'draft', label: '草稿' },
              { key: 'pending_review', label: '待审核' },
              { key: 'supplement_required', label: '需补充' },
              { key: 'rejected', label: '已驳回' },
              { key: 'approved', label: '已通过' },
              { key: 'converted_to_project', label: '已转项目' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveStatus(s.key)}
                className={`text-xs px-3.5 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeStatus === s.key
                    ? 'bg-foreground-950 text-background-50 font-medium'
                    : 'border border-background-300/60 text-foreground-500 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Applications List */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-20">
                <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin mb-3 inline-block"></i>
                <p className="text-sm text-foreground-500">数据加载中...</p>
              </div>
            ) : error ? (
              <div className="text-center py-16 bg-red-50 rounded-xl border border-red-100 p-6">
                <i className="ri-error-warning-line text-3xl text-red-500 mb-2 inline-block"></i>
                <p className="text-sm text-red-700 mb-3">{error}</p>
                <button
                  onClick={() => loadApplications(true)}
                  className="px-4 py-2 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all cursor-pointer"
                >
                  重新加载
                </button>
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="text-center py-20 glass-card rounded-xl border border-dashed border-gray-200">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500 mb-3">暂无发行申请</p>
                <Link
                  to="/apply"
                  className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer font-medium"
                >
                  创建第一个发行申请
                </Link>
              </div>
            ) : (
              filteredApplications.map((app) => {
                const rawStatus = (app.status || '').toUpperCase();
                const statusInfo = statusConfig[rawStatus] || statusConfig[app.status || ''] || {
                  label: app.status || '未知',
                  color: 'bg-foreground-100 text-foreground-600 border border-gray-200',
                  focus: '管理短剧发行申请',
                };
                const displayId = app.application_no || app.id || '-';
                const detailLink = `/applications/${app.id}`;
                const isLoadingThis = actionLoadingId === app.id;

                return (
                  <div
                    key={app.id || app.application_no}
                    className="glass-card rounded-xl p-5 border border-gray-200/70 hover:border-primary-200 transition-all flex flex-col gap-4"
                  >
                    {/* Top Row: ID, Title, Status */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs text-foreground-400 font-mono flex-shrink-0">{displayId}</span>
                        <h3 className="text-base font-bold text-foreground-950 truncate">
                          {app.title || '未命名项目'}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>

                    {/* Middle: Focus Summary / Page Focus Hint */}
                    <div className="bg-gray-50/80 rounded-lg p-3 text-xs text-foreground-600 flex items-start gap-2 border border-gray-100">
                      <i className="ri-information-line text-primary-600 text-sm flex-shrink-0 mt-0.5"></i>
                      <div className="flex-1">
                        <span className="font-medium text-foreground-800">页面重点：</span>
                        <span>{statusInfo.focus}</span>
                      </div>
                    </div>

                    {/* Meta info & Intent channels */}
                    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-foreground-500 pt-1 border-t border-gray-100">
                      <div className="flex flex-wrap items-center gap-3">
                        {app.content_type_code && <span>类别: {formatContentType(app.content_type_code)}</span>}
                        {app.submitted_at && <span>提交于: {formatDate(app.submitted_at)}</span>}
                        {app.intent_channel_ids && app.intent_channel_ids.length > 0 && (
                          <div className="flex items-center gap-1">
                            <span>意向渠道:</span>
                            <div className="flex flex-wrap gap-1">
                              {app.intent_channel_ids.map((ch) => (
                                <span key={ch} className="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-foreground-600 text-2xs">
                                  {getChannelNameById(ch, ch)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Executable Actions according to section 5.4 */}
                      <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0 ml-auto">
                        {/* DRAFT 草稿操作: 编辑、删除、提交 */}
                        {rawStatus === 'DRAFT' && app.id && (
                          <>
                            <Link
                              to={`/apply?id=${app.id}`}
                              className="px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-foreground-700 text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                            >
                              编辑
                            </Link>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmApp(app)}
                              disabled={isLoadingThis}
                              className="px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              删除
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSubmit(app.id!)}
                              disabled={isLoadingThis}
                              className="px-3.5 py-1.5 rounded-md bg-primary-500 hover:bg-primary-400 text-foreground-950 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1 shadow-2xs disabled:opacity-50"
                            >
                              {isLoadingThis && <i className="ri-loader-4-line animate-spin"></i>}
                              <span>提交审核</span>
                            </button>
                          </>
                        )}

                        {/* PENDING_REVIEW 待审核操作: 查看 */}
                        {['PENDING', 'PENDING_REVIEW', 'UNDER_REVIEW', 'IN_REVIEW', 'SUBMITTED', 'REVIEWING'].includes(rawStatus) && (
                          <Link
                            to={detailLink}
                            className="px-3.5 py-1.5 rounded-md bg-foreground-900 hover:bg-foreground-800 text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
                          >
                            <span>查看详情</span>
                            <i className="ri-arrow-right-s-line"></i>
                          </Link>
                        )}

                        {/* SUPPLEMENT_REQUIRED 需补充操作: 仅补充指定材料/剧集、再次提交 */}
                        {['SUPPLEMENT_REQUIRED', 'NEED_SUPPLEMENT'].includes(rawStatus) && app.id && (
                          <>
                            <Link
                              to={`/apply?id=${app.id}&mode=supplement`}
                              className="px-3.5 py-1.5 rounded-md bg-orange-500 hover:bg-orange-400 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1 shadow-2xs"
                            >
                              <span>补充材料/剧集</span>
                            </Link>
                            <Link
                              to={`${detailLink}?tab=review`}
                              className="px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-foreground-700 text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                            >
                              查看意见
                            </Link>
                          </>
                        )}

                        {/* REJECTED 已驳回操作: 查看、复制为新申请 */}
                        {['REJECTED', 'REFUSED'].includes(rawStatus) && app.id && (
                          <>
                            <Link
                              to={`${detailLink}?tab=review`}
                              className="px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-foreground-700 text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                            >
                              查看驳回原因
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleCopy(app.id!)}
                              disabled={isLoadingThis}
                              className="px-3.5 py-1.5 rounded-md bg-primary-500 hover:bg-primary-400 text-foreground-950 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1 shadow-2xs disabled:opacity-50"
                            >
                              {isLoadingThis && <i className="ri-loader-4-line animate-spin"></i>}
                              <span>复制为新申请</span>
                            </button>
                          </>
                        )}

                        {/* APPROVED 已通过操作: 查看 */}
                        {['APPROVED', 'PASSED'].includes(rawStatus) && (
                          <Link
                            to={detailLink}
                            className="px-3.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
                          >
                            <span>查看通过结果</span>
                            <i className="ri-arrow-right-s-line"></i>
                          </Link>
                        )}

                        {/* CONVERTED_TO_PROJECT 已转项目操作: 跳转发行项目 */}
                        {['CONVERTED_TO_PROJECT', 'SIGNED', 'COMPLETED'].includes(rawStatus) && (
                          <Link
                            to={app.project_id ? `/projects/${app.project_id}` : '/projects'}
                            className="px-3.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-1 shadow-2xs"
                          >
                            <i className="ri-external-link-line"></i>
                            <span>跳转发行项目</span>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}